import { SAGE_LINKS } from "@/lib/sageLinks";

// MusicStudioSignBlock — Resilience Music Studio's signature block.
// Renders ONLY on the score-voice product page (Music Studio).
// Model reversal from 2026-08-12: this product is Resilience-native and
// deliberately does NOT carry the DIY-First footer.
export default function MusicStudioSignBlock() {
  return (
    <section
      data-testid="music-studio-sign-block"
      className="border-t border-[#27272a] bg-[#0a0a0b] py-16 px-6 sm:px-10"
    >
      <div className="max-w-[1000px] mx-auto">
        <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-6">
          Two Paths · One Music Studio
        </p>
        <h2
          className="font-display uppercase leading-[1.05] text-[#ededed]"
          style={{ fontSize: "clamp(1.75rem, 3.4vw, 2.75rem)", letterSpacing: "-0.005em" }}
        >
          Buy the service. Or sign as an artist and let us produce you.
        </h2>
        <p className="font-body text-lg text-[#a1a1aa] leading-relaxed mt-6">
          The Music Studio at Resilience runs two doors. Every buyer picks the
          door that fits.
        </p>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
          {/* Path A — buy the service outright */}
          <div
            data-testid="music-studio-path-service"
            className="border border-white/[0.09] bg-black/40 p-8 flex flex-col"
          >
            <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-4">
              Path A · Service Only
            </p>
            <h3 className="font-display uppercase text-3xl text-[#ededed] leading-tight mb-4">
              Buy the tier. Own the master.
            </h3>
            <p className="font-body text-base text-[#c8c8ce] leading-relaxed">
              Purchase any tier above (Cue Pack through Signature Sound
              Package). You own the finished work outright, no royalty attached,
              no career deal, no long paperwork.
            </p>
            <ul className="mt-6 space-y-2 font-body text-sm text-[#a1a1aa]">
              <li className="flex gap-3">
                <span className="text-[#ff5a00]">·</span>
                <span>Full stems delivered on completion</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#ff5a00]">·</span>
                <span>Zero royalty split</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#ff5a00]">·</span>
                <span>Turnaround measured in days-to-weeks, not months</span>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-white/5">
              <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
                Boutique top tier
              </span>
              <div className="font-display text-4xl text-[#ededed] mt-1">$99,000</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b] mt-1">
                Or drive the tool yourself on Sage · $60,000 DIY floor →
              </div>
              <a
                href={SAGE_LINKS["score-voice"]}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="music-studio-sage-diy-link"
                className="mt-4 inline-flex font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa] hover:text-[#ff5a00] transition-colors"
              >
                Sage self-serve →
              </a>
            </div>
          </div>

          {/* Path B — sign as an artist */}
          <div
            data-testid="music-studio-path-sign"
            className="border border-[#ff5a00]/40 bg-black/40 p-8 flex flex-col relative"
          >
            <div className="absolute -top-3 left-6 bg-[#ff5a00] text-[#050505] font-mono text-[9px] uppercase tracking-[0.32em] px-3 py-1">
              Most Ethical House On The Planet
            </div>
            <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-4">
              Path B · Sign as an artist
            </p>
            <h3 className="font-display uppercase text-3xl text-[#ededed] leading-tight mb-4">
              Let us produce you. We keep 9% of royalties.
            </h3>
            <p className="font-body text-base text-[#c8c8ce] leading-relaxed">
              If you are an artist ready to be signed — sign with Resilience.
              We produce, we record, we mix, we master, we distribute. In
              exchange, we keep{" "}
              <span className="text-[#ededed] font-medium">9% of royalties</span>{" "}
              — a fraction of the industry standard label cut (15%–50%).
              You keep the other 91% and the master.
            </p>
            <ul className="mt-6 space-y-2 font-body text-sm text-[#a1a1aa]">
              <li className="flex gap-3">
                <span className="text-[#ff5a00]">·</span>
                <span>Full in-house production, mix, master</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#ff5a00]">·</span>
                <span>Distribution across streaming + physical</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#ff5a00]">·</span>
                <span>You retain 91% of royalties + own the master</span>
              </li>
              <li className="flex gap-3">
                <span className="text-[#ff5a00]">·</span>
                <span>Modest 9% keeps our studio running · nothing more</span>
              </li>
            </ul>
            <div className="mt-6 pt-6 border-t border-[#ff5a00]/20">
              <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#52525b]">
                Industry standard
              </span>
              <div className="font-display text-[#a1a1aa] line-through text-2xl mt-1">
                15% – 50% royalty to the label
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] mt-2 block">
                Resilience
              </span>
              <div className="font-display text-4xl text-[#ff5a00] mt-1">9% royalty</div>
            </div>
            <a
              href="/contact?ref=music-studio-sign"
              data-testid="music-studio-sign-cta"
              className="mt-8 inline-flex items-center justify-center font-mono text-[11px] uppercase tracking-[0.32em] bg-[#ff5a00] text-[#050505] px-6 py-3 hover:bg-[#ff8f00] transition-colors"
            >
              Sign as a Resilience artist →
            </a>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-white/5">
          <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-4">
            A note from the Founder
          </p>
          <p className="font-body text-lg text-[#ededed] leading-relaxed italic">
            "The boutique that kills the industry. Feature score at $99K instead
            of a working composer's $225K. Ten-character voice cast in-house.
            9% royalty on artist signings when the industry floor is 15-50%.
            And if you would rather drive the same tool yourself, Sage will
            do it for $60K DIY. Two doors. Two prices. One ethical house."
          </p>
        </div>
      </div>
    </section>
  );
}
